import { select, put, take, fork, takeEvery, cancel, takeLatest, spawn, call } from 'redux-saga/effects';
import { navigate } from '@/domain/router/redux/reduxActions';
import { generatePath } from 'react-router-dom';
import * as Env from '@/domain/env/sagas';
import * as Router from '@/domain/router';
import * as ACL from '@/domain/restriction';
import ROUTES_PATH from '@/router/routesPathConfig';
import { getCurrentRoute } from '@/domain/router/utils';
import { expiredAction, cognitoAuthInitAction } from '@/domain/env';
import { storage } from '@/lib/storage';
import { notify } from '@/lib/errorLogger';
import { cognitoAuthHandler } from '@/domain/env/sagas';
import { Auth } from 'aws-amplify';
import { isAuthenticated, redirectToLoginAppWithBackUrl } from '@/initializers/awsAmplify/helpers';
import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { pipe, Option } from 'effect';

const PROHIBITED_BACK_URL_ROUTES = ['resetpasswd', 'setpasswd', 'forgotPassword'];
const CACHE_EXPIRY_TIME_MS = 24 * 60 * 60 * 1000; // 24 hours

const getCurrentUrl = () => window.location.pathname + window.location.search;

const getAllowedRoute = (restriction) => {
  const route = getCurrentRoute();
  return route ? ACL.check(route.restriction, restriction) : null;
};

const isProhibitedBackUrl = (route) => (route ? PROHIBITED_BACK_URL_ROUTES.includes(route.id) : true);

const clearOldCacheDocuments = async () => {
  try {
    const cache = await caches.open('documents');
    if (cache) {
      cache.keys().then((keys) => {
        keys.forEach(async (request) => {
          const response = await cache.match(request);
          if (response) {
            const cacheAge = Date.parse(new Date()) - Date.parse(response.headers.get('date'));
            if (cacheAge > CACHE_EXPIRY_TIME_MS) {
              await cache.delete(request);
            }
          }
        });
      });
    }
  } catch (err) {
    notify.captureEvent(err);
  }
};

function* watchSignIn() {
  const task = yield takeLatest(Env.action.signInAction.type, Env.ensureSignInWithUserProfile);
  yield take(Env.action.signInAction.success);
  yield put(Router.completeEmptyAction('auth'));
  yield cancel(task);
  yield spawn(watchSignOut);
}

function* watchSignOut() {
  yield takeEvery(Env.action.signOutAction.type, Env.ensureSignOut);
  const { payload } = yield take(Env.action.signOutAction.success);
  clearOldCacheDocuments();
  // we set backUrl if we were not logged out by 403 in interceptor. In case of 403 we
  // should not set it as user will be redirected to backUrl and 403 indicates he has
  // no access so we are running into login logout cycle
  try {
    yield call([Auth, 'signOut']);
  } catch (error) {
    console.log(error);
  }

  if (
    (payload && payload.reason === process.env.REACT_APP_ACCESS_DENIED_CODE) ||
    isProhibitedBackUrl(getCurrentRoute())
  ) {
    storage().backUrl().set('');
    const loginUrl = getRuntimeConfig().cognito.REACT_APP_COGNITO_LOGIN_URL;
    const urlWithReason = pipe(
      payload?.accessDeniedReasonType,
      Option.fromNullable,
      Option.map((reason) => `${loginUrl}?reason=${reason}`),
      Option.getOrElse(() => loginUrl),
    );
    window.location = urlWithReason;
  } else {
    // SSO, in case user is logged-out when expired token is used to get userProfile
    storage().backUrl().set(getCurrentUrl());
    redirectToLoginAppWithBackUrl();
  }
}

function* watchExpired() {
  yield put(navigate.replace(generatePath(ROUTES_PATH.EXPIRED.absolute)));
}

export default function* ensureSign() {
  clearOldCacheDocuments();
  yield takeLatest(expiredAction.type, watchExpired);
  // watches signIn event emited by amplify event Hub
  yield takeLatest(cognitoAuthInitAction.type, cognitoAuthHandler);
  if (yield call(isAuthenticated)) {
    yield fork(watchSignOut);
  } else {
    const restriction = yield select(ACL.myRestriction);

    // the match function used in the isMatchRoute function does not know how to process the corresponding path="*"
    // for route 404 and returns an error. Therefore, we catch an error here and direct the user to the login page
    try {
      const allowedRoute = getAllowedRoute(restriction);

      if (!(allowedRoute && allowedRoute.id !== '404')) {
        // persist backurl for SSO
        const currentURL = getCurrentUrl();
        storage().backUrl().set(currentURL);
        redirectToLoginAppWithBackUrl();
      }
    } catch {
      window.location = getRuntimeConfig().cognito.REACT_APP_COGNITO_LOGIN_URL;
    }
    yield fork(watchSignIn);
  }
}
